import React, { useId } from "react";
import { Box, CircularProgress, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CustomTypography from "../form/CustomTypography";

const hexToRgb = (hex: string) => {
  const normalized = hex.replace("#", "");
  const full =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => c + c)
          .join("")
      : normalized;
  const num = parseInt(full, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
};

const rgbToHex = (r: number, g: number, b: number) =>
  `#${[r, g, b]
    .map((channel) =>
      Math.round(Math.min(255, Math.max(0, channel)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;

export const mixColors = (color1: string, color2: string, ratio: number) => {
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);
  const t = Math.min(1, Math.max(0, ratio));
  return rgbToHex(
    c1.r + (c2.r - c1.r) * t,
    c1.g + (c2.g - c1.g) * t,
    c1.b + (c2.b - c1.b) * t,
  );
};

/** Performance color: red below 50, orange 50–70, green above 70. */
export const getGradientColorAtScore = (
  score: number,
  gradientColors: string[],
): string => {
  if (!gradientColors.length) return "#000000";

  const red = gradientColors[0];
  const orange = gradientColors[1] ?? red;
  const green = gradientColors[2] ?? gradientColors[gradientColors.length - 1];

  const clamped = Math.min(100, Math.max(0, score));

  if (clamped > 70) return green;
  if (clamped >= 50) return orange;
  return red;
};

interface CustomCircularProgressProps {
  size?: number;
  thickness?: number;
  progress: number;
  label?: string;
  bgColor?: string;
  progressColor?: string;
  /** Stroke gradient from arc start to end (e.g. red → orange → green). */
  gradientColors?: string[];
  /** Lighter track gradient; defaults to faded gradientColors. */
  gradientBgColors?: string[];
  /** Optional stop offsets (0–1) for each color in gradientColors. */
  gradientStops?: number[];
  /** Opacity for auto-derived gradientBgColors (default 0.25). */
  gradientBgOpacity?: number;
  labelColor?: string;
  valueColor?: string;

  labelFontSize?: number | string;
  PercentFontSize?: number | string;
}

const CustomCircularProgress: React.FC<CustomCircularProgressProps> = (
  props,
) => {
  const theme = useTheme();
  const baseId = useId().replace(/:/g, "");
  const gradientId = `${baseId}-progress`;
  const gradientBgId = `${baseId}-bg`;

  const {
    size = 200,
    thickness = 4,
    progress,
    label = "",
    bgColor = theme.palette.customColors.lightBlue,
    progressColor = theme.palette.customColors.lightBlue,
    gradientColors,
    gradientBgColors,
    gradientStops,
    gradientBgOpacity = 0.25,
    labelColor = theme.palette.customColors.greyTextColor,
    valueColor = theme.palette.customColors.blackBorder,
    labelFontSize = "1rem",
    PercentFontSize = "0.8rem",
  } = props;

  const useGradient = Boolean(gradientColors?.length);
  const resolvedBgGradientColors = useGradient
    ? (gradientBgColors ??
      gradientColors!.map((color) => alpha(color, gradientBgOpacity)))
    : undefined;
  const progressStroke = useGradient ? `url(#${gradientId})` : progressColor;
  const bgStroke = useGradient ? `url(#${gradientBgId})` : undefined;

  const renderGradientStops = (colors: string[], id: string) => (
    <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
      {colors.map((color, index) => {
        const offset =
          gradientStops?.[index] ??
          (colors.length === 1 ? 0 : index / (colors.length - 1));
        return (
          <stop
            key={`${color}-${index}`}
            offset={`${offset * 100}%`}
            stopColor={color}
          />
        );
      })}
    </linearGradient>
  );

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        width: size,
        height: size,
      }}
    >
      {useGradient && (
        <svg width={0} height={0} aria-hidden>
          <defs>
            {renderGradientStops(gradientColors!, gradientId)}
            {renderGradientStops(resolvedBgGradientColors!, gradientBgId)}
          </defs>
        </svg>
      )}

      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        sx={{
          color: useGradient ? "transparent" : bgColor,
          [`& .MuiCircularProgress-circle`]: useGradient
            ? { stroke: bgStroke, strokeLinecap: "round" }
            : undefined,
        }}
      />

      <CircularProgress
        variant="determinate"
        value={Math.min(progress, 100)}
        size={size}
        thickness={thickness}
        sx={{
          color: useGradient ? "transparent" : progressColor,
          position: "absolute",
          left: 0,
          [`& .MuiCircularProgress-circle`]: {
            stroke: progressStroke,
            strokeLinecap: "round",
          },
        }}
      />

      <Box
        sx={{
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {label && (
          <CustomTypography
            variant="body2"
            style={{
              color: labelColor,
              fontWeight: 500,
              fontSize: labelFontSize,
            }}
          >
            {label}
          </CustomTypography>
        )}
        <CustomTypography
          variant="heading1"
          style={{ color: valueColor, fontSize: PercentFontSize }}
        >
          {`${progress}%`}
        </CustomTypography>
      </Box>
    </Box>
  );
};

export default CustomCircularProgress;
